import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetProductReviewsQuery, useAddBetaCommentMutation } from '../../../redux/apis/endpoints/reviewApi';
import { selectCurrentUser } from '../../../redux/slices/authSlice';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorMessage from '../../../components/common/ErrorMessage';
import UserProfilePic from '../../../components/common/UserProfilePic';
import Button from '../../../components/ui/Button';
import { StarIcon } from '@heroicons/react/24/solid';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

/** Renders a single review card (launch mode with stars) */
const ReviewCard = ({ review }) => (
  <div className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
    <div className="flex items-center mb-2">
      {[...Array(5)].map((_, i) => (
        <StarIcon
          key={i}
          className={`h-5 w-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
    <p className="text-gray-700 leading-relaxed mb-3">{review.comment}</p>
    <div className="flex items-center text-sm text-gray-500 mb-3">
      <UserProfilePic
        user={review.customers?.[0]}
        className="w-6 h-6"
        altText={review.customers?.[0]?.username || 'User'}
      />
      <span className="ml-2">{review.customers?.[0]?.username || review.user?.username || 'Anonymous User'}</span>
      <span className="mx-2">|</span>
      <span>{formatDate(review.createdAt || review.created_at)}</span>
    </div>
    {review.pictures && review.pictures.length > 0 && (
      <div className="mt-3 flex flex-wrap gap-3">
        {review.pictures.map(pic => (
          <img
            key={pic.id}
            src={pic.url}
            alt={`Review image ${pic.id}`}
            className="w-20 h-20 rounded-md object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
          />
        ))}
      </div>
    )}
  </div>
);




/** Renders a single comment card (beta mode, no stars) */
const CommentCard = ({ comment, currentUser }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOwnComment = currentUser && comment.customers?.[0]?.id === currentUser.id;
  const userForAvatar = isOwnComment ? currentUser : comment.customers?.[0];
  const text = comment.comment || '';
  const isLong = text.length > 80;

  return (
    <div className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <UserProfilePic
            user={userForAvatar}
            className="w-9 h-9"
            altText={comment.customers?.[0]?.username || 'User'}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 text-sm">
              {comment.customers?.[0]?.username || 'Anonymous'}
            </span>
            <span className="text-xs text-gray-400">
              {formatDate(comment.createdAt || comment.created_at)}
            </span>
          </div>
          {isLong && !isExpanded ? (
            <>
              <p className="text-gray-700 text-sm leading-relaxed" style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '80%'
              }}>
                {text}
              </p>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setIsExpanded(true)}
                onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(true)}
                style={{ color: '#0d9488', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem' }}
                onMouseEnter={(e) => e.target.style.color = '#115e59'}
                onMouseLeave={(e) => e.target.style.color = '#0d9488'}
              >...more</span>
            </>
          ) : (
            <>
              <p className="text-gray-700 text-sm leading-relaxed" style={{ wordBreak: 'break-word' }}>
                {text}
              </p>
              {isLong && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsExpanded(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(false)}
                  style={{ color: '#0d9488', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem' }}
                  onMouseEnter={(e) => e.target.style.color = '#115e59'}
                  onMouseLeave={(e) => e.target.style.color = '#0d9488'}
                >show less</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Comment input form for logged-in users */
const CommentForm = ({ onSubmit, isSubmitting, currentUser }) => {
  const [commentText, setCommentText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    onSubmit(commentText.trim());
    setCommentText('');
  };

  return (
    <form onSubmit={handleSubmit} className="mb-10">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <UserProfilePic
            user={currentUser}
            className="w-9 h-9"
            altText={currentUser?.username || 'You'}
          />
        </div>
        <input
          type="text"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Leave a comment..."
          className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
          disabled={isSubmitting}
        />
        <div className="flex-shrink-0">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!commentText.trim()}
            isLoading={isSubmitting}
          >
            Post
          </Button>
        </div>
      </div>
    </form>
  );
};

/**
 * Comments section for product pages.
 * - Beta mode: Any logged-in user can comment. Shows simple text comments.
 * - Launch mode: Shows order-specific reviews with star ratings.
 */
const ProductReviewsDisplay = ({ productId }) => {
    const currentUser = useSelector(selectCurrentUser);
  const [showAllComments, setShowAllComments] = useState(false);

  // Launch mode: fetch product reviews
  const {
    data: reviewsData,
    isLoading: isReviewsLoading,
    isError: isReviewsError,
    error: reviewsError
  } = useGetProductReviewsQuery(
    { productId, strapiV3: true, sort: 'createdAt:desc', pageSize: 100 },
    { skip: !productId }
  );

  const [addBetaComment, { isLoading: isPosting }] = useAddBetaCommentMutation();

  const handlePostComment = async (text) => {
    if (!currentUser) {
      toast.error('Please log in to comment');
      return;
    }
    try {
      await addBetaComment({
        comment: text,
        listingId: productId,
        customerId: currentUser.id,
      }).unwrap();
      toast.success('Comment posted!');
    } catch (err) {
      console.error('Failed to post comment:', err);
      toast.error('Failed to post comment. Please try again.');
    }
  };

  const isLoading = isReviewsLoading;
  const isError = isReviewsError;
  const error = reviewsError;

  if (isLoading) return <div className="py-8"><LoadingSpinner /></div>;
  if (isError) return <ErrorMessage message={error?.data?.message || 'Could not load comments.'} />;

  // Launch mode: reviews with stars
  
    const reviews = reviewsData || [];
    if (reviews.length === 0) return null;
    return (
      <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
        <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">Reviews</h2>
        <div className="space-y-8">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </div>
    );
  
};

export default ProductReviewsDisplay;
